import {Download, GetApp, KeyboardArrowDown, KeyboardArrowUp, UploadFile} from "@mui/icons-material";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import SearchIcon from "@mui/icons-material/Search";
import {Box, Button, CircularProgress, FormControl, Grid, IconButton, InputLabel, MenuItem, Pagination, Paper, Select, Stack, Tab, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tabs, TextField, Typography} from "@mui/material";
import Chip from "@mui/material/Chip";
import {DatePicker, LocalizationProvider} from "@mui/x-date-pickers";
import {AdapterMoment} from "@mui/x-date-pickers/AdapterMoment";
import {formatDate} from "app/utils/date";
import ExcelJS from "exceljs";
import moment from "moment";
import React, {useEffect, useMemo, useState} from "react";
import {toast} from "react-toastify";
import {axios_tenant} from "../../../axios";
import UploadDocModal from "./UploadDocModal";

const getStatusColor = (status) => {
    switch (status) {
        case "Under Review":
            return "#EA8407";
        case "In Progress":
            return "#FCD426";
        case "To Do":
            return "#0D93D2";
        case "Completed":
            return "#24BC7C";
        default:
            return "inherit";
    }
};

const DOCUMENTS_LIST = [
  "International Application Status Report (XML)",
  "International Application Status Report (PDF)",
  "(IB/371) Notification Relating to Declaration Made Under PCT Rule 4.17",
  "Declaration",
  "Initial Publication with ISR",
  "Initial Publication without ISR",
  "(ISA/210) International Search Report (PDF)",
  "(ISA/210) International Search Report (XML)",
  "(ISA/237) Written Opinion of the International Searching Authority",
  "Translation of the ISR (PDF)",
  "Translation of the ISR (XML)",
  "(RO/101) Request Form",
  "(IB/301) Notification of receipt of record copy",
  "(IB/306) Notification of the Recording of a Change",
  "(RO/105) Notification of the International Application Number and of the International Filing Date",
  "Biological sequence listing submitted with application",
  "(IB/373) International Preliminary Report on Patentability",
  "English Translation of the Written Opinion of the International Searching Authority"
];

const documentFieldMap = {
    "(RO/101) Request Form": "ro101Status",
    "Initial Publication with ISR": "initialPublicationStatus",
    "(ISA/210) International Search Report (PDF)": "isa210Status", 
    "(ISA/210) International Search Report (XML)": "isa210XMLStatus",
    "(ISA/237) Written Opinion of the International Searching Authority": "isa237Status",
    "International Application Status Report (XML)": "iasrStatus",
    "International Application Status Report (PDF)": "iasrPDFStatus",
    "Translation of the ISR (PDF)": "tisrPDFStatus",
    "Translation of the ISR (XML)": "tisrXMLStatus",
    "(IB/371) Notification Relating to Declaration Made Under PCT Rule 4.17": "ib371Status",
    "Declaration": "declarationStatus",
    "Initial Publication without ISR": "initialPubWithoutISRStatus",
    "(IB/301) Notification of receipt of record copy": "ib301ReceiptCopyStatus",
    "(IB/306) Notification of the Recording of a Change": "ib306ChangeRecordStatus",
    "(RO/105) Notification of the International Application Number and of the International Filing Date": "ro105AppNoDateStatus",
    "Biological sequence listing submitted with application": "seqListStatus",
    "(IB/373) International Preliminary Report on Patentability": "ib373Status",
    "English Translation of the Written Opinion of the International Searching Authority": "woEnglishStatus"
};

const FILING_TYPE_NAME_MAP = {
    "371application": "371 Filing",
    "bypasscontinuation": "Bypass Con.",
}

function extractUsername(sourceData) {
    let username = "";
    if (sourceData) {
        username = sourceData?.userName || (sourceData?.source?.toLowerCase() === "existing" ? "System" : "" ) || "";
    }
    return username;
}

const FilingPackageTable = ({requests, loadRequests, loading, setLoading}) => {
    const [tab, setTab] = React.useState(0);
    const [searchTerm, setSearchTerm] = useState("");
    const [receivedOn, setReceivedOn] = useState("");
    const [status, setStatus] = useState("");

    const [page, setPage] = React.useState(1);
    const [rowsPerPage, setRowsPerPage] = useState(5);

    const [selectedDocument, setSelectedDocument] = useState(null);
    const [expandedRequest, setExpandedRequest] = React.useState(null);
    const [selectedRequestId, setSelectedRequestId] = useState(null);
    const [modalOpen, setModalOpen] = useState(false);

    const [pendingRequests, setPendingRequests] = useState(requests.filter((request) => request.status !== "Completed")).sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

    const [completedRequests, setCompletedRequests] = useState(requests.filter((request) => request.status.includes("Completed")).sort((a, b) => new Date(b.metadata.updatedAt) - new Date(a.metadata.updatedAt)));

    const filteredRequests = useMemo(() => {
        let selectedTabRequests = tab === 0 ? pendingRequests : completedRequests;
        // Filter by search term
        return selectedTabRequests
            .filter((request) => {
                if (!searchTerm) return true;
                const searchTermLower = searchTerm.toLowerCase().trim();
                return request.requestId.toLowerCase().includes(searchTermLower) || request.tenant.toLowerCase().includes(searchTermLower) || request.metadata.pctNumber.toLowerCase().includes(searchTermLower) || request.requestId.toLowerCase().includes(searchTermLower) || request.filingType.toLowerCase().includes(searchTermLower);
            })
            .filter((request) => {
                if (!receivedOn) return true;
                return moment(request.createdAt).format("YYYY-MM-DD") === receivedOn;
            })
            .filter((request) => {
                if (!status) return true;
                return request.status === status;
            });
    }, [pendingRequests, completedRequests, tab, searchTerm, receivedOn, status]);

    const paginatedRequests = useMemo(() => {
        if (filteredRequests.length === 0) return []; // Edge case

        const sortedRequests = [...filteredRequests];

        return sortedRequests.slice(
            (page - 1) * rowsPerPage, // Map 1-based page to zero-based index
            page * rowsPerPage // Correct upper bound
        );
    }, [filteredRequests, page, rowsPerPage]);

    useEffect(() => {
        setPage(1);
        setPendingRequests(requests.filter((request) => !request.status.includes("Completed")).sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)));
        setCompletedRequests(requests.filter((request) => request.status.includes("Completed")).sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt)));
    }, [tab]);

    useEffect(() => {
        const pending = requests.filter((request) => !request.status.includes("Completed")).sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
        const completed = requests.filter((request) => request.status.includes("Completed")).sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt));

        setPendingRequests(pending);
        setCompletedRequests(completed);

        if ((tab === 0 && page > Math.ceil(pending.length / rowsPerPage)) || (tab === 1 && page > Math.ceil(completed.length / rowsPerPage))) {
            setPage(1);
        }
    }, [requests]);

    const handleTabChange = (event, newValue) => {
        setTab(newValue);
    };

    const handleUploadClick = (request, requestId, document) => {
        setSelectedRequestId(requestId);
        setSelectedDocument(document);
        setModalOpen(true);
    };

    const handleCloseModal = async () => {
        setModalOpen(false);
        setSelectedRequestId(null);
    };

    const toggleExpand = (requestId) => {
        setExpandedRequest(expandedRequest === requestId ? null : requestId);
    };

    const handleUpdateStatus = async (request) => {
        setLoading(true);
        try {
            await axios_tenant.put(`/reference-downloader/pctrequests/status`, {
                requestId: request.requestId
            });
            await loadRequests();
            toast.success("Request status updated successfully");
        } catch (error) {
            toast.error("Failed to update request status");
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (request) => {
        setLoading(true);
        try {
            await axios_tenant.put(`/reference-downloader/pctrequests/submit`, {
                requestId: request.requestId
            });
            await loadRequests();
            toast.success("Request status updated successfully");
        } catch (error) {
            toast.error("Failed to update request status");
        } finally {
            setLoading(false);
        }
    };

    const searchHandler = (event) => {
        setSearchTerm(event.target.value);
        setPage(1);
    };

    const downloadFiles = async (requestId, file = null) => {
        setLoading(true);
        console.log(file);
        const baseUrl = `/reference-downloader/pctrequests/download/${requestId}`;
        const requestUrl = file ? `${baseUrl}?file=${encodeURIComponent(documentFieldMap[file])}` : baseUrl;
        try {
            const response = await axios_tenant.get(requestUrl, {
                responseType: "blob"
            });

            const contentDisposition = response.headers["content-disposition"];
            const filenameMatch = contentDisposition?.match(/filename="?(.+?)"?$/);
            const filename = filenameMatch ? filenameMatch[1] : "download";

            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", filename);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            toast.error("Failed to download documents");
        } finally {
            setLoading(false);
        }
    };

    const handleDownloadRequestsSpreadsheet = async () => {
        setLoading(true);
        try {
            const workbook = new ExcelJS.Workbook();
            const worksheet = workbook.addWorksheet("Requests");
            worksheet.addRow(["Tenant name", "PCT Number", "Filing Type", "Request ID", "Request Date & Time", "Status", "Completion Date & Time", "(RO/101) Request Form", "Initial Publication with ISR (PDF)", "(ISA/210) International Search Report (PDF)", "(ISA/237) Written Opinion of the International Searching Authority", "International Application Status Report (XML)", "International Application Status Report (PDF)", "(ISA/210) International Search Report (XML)", "Translation of the ISR (PDF)", "Translation of the ISR (XML)", "(IB/371) Notification Relating to Declaration Made Under PCT Rule 4.17", "Declaration", "Initial Publication without ISR", "(IB/301) Notification of receipt of record copy", "(IB/306) Notification of the Recording of a Change", "(RO/105) Notification of the International Application Number and of the International Filing Date", "Biological sequence listing submitted with application", "(IB/373) International Preliminary Report on Patentability", "English Translation of the Written Opinion of the International Searching Authority"]);
            worksheet.columns = [{width: 25}, {width: 25}, {width: 25}, {width: 25}, {width: 25}];
            let selectedTabRequests = tab === 0 ? pendingRequests : completedRequests;
            selectedTabRequests.forEach((request) => {
                let dateTime = `${formatDate(request.createdAt)} ${new Intl.DateTimeFormat("en-US", {hour: "2-digit", minute: "2-digit", hour12: true}).format(new Date(request.createdAt))}`;
                let completedDateTime = `${formatDate(request.updatedAt)} ${new Intl.DateTimeFormat("en-US", {hour: "2-digit", minute: "2-digit", hour12: true}).format(new Date(request.updatedAt))}`;
                let documentSource = request.documentSource || {};
                const filingTypeName = FILING_TYPE_NAME_MAP[request.filingType];
                const row = worksheet.addRow([request.tenant, request.metadata.pctNumber, filingTypeName, request.requestId, dateTime, request.status, completedDateTime, extractUsername(documentSource.ro101), extractUsername(documentSource.initialPublication), extractUsername(documentSource.isa210), extractUsername(documentSource.isa237), extractUsername(documentSource.iasr), extractUsername(documentSource.iasrPDF), extractUsername(documentSource.isa210XML), extractUsername(documentSource.tisrPDF), extractUsername(documentSource.tisrXML), extractUsername(documentSource.ib371), extractUsername(documentSource.declaration), extractUsername(documentSource.initialPubWithoutISR), extractUsername(documentSource.ib301ReceiptCopy), extractUsername(documentSource.ib306ChangeRecord), extractUsername(documentSource.ro105AppNoDate), extractUsername(documentSource.seqList), extractUsername(documentSource.ib373), extractUsername(documentSource.woEnglish)]);
                row.font = {bold: true};
            });

            const buffer = await workbook.xlsx.writeBuffer();
            const blob = new Blob([buffer], {type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"});
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            const today = new Date();
            const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
            const formattedDate = `${today.getDate().toString().padStart(2, "0")}-${monthNames[today.getMonth()]}-${today.getFullYear()}`;
            link.setAttribute("download", `371_Filing_Package_Report_${formattedDate}.xlsx`);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            toast.error("Error downloading requests spreadsheet");
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <Box sx={{width: "100%"}}>
                {loading && (
                    <Box
                        sx={{
                            position: "absolute",
                            top: 0,
                            left: 0,
                            width: "100%",
                            height: "100%",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            backgroundColor: "rgba(255, 255, 255, 0.7)"
                        }}
                    >
                        <CircularProgress/>
                    </Box>
                )}
                <Box sx={{borderBottom: 1, borderColor: "divider", mb: 2}}>
                    <Tabs value={tab} onChange={handleTabChange} TabIndicatorProps={{style: {background: "#06F"}}}>
                        <Tab label="Pending"/>
                        <Tab label="Completed"/>
                    </Tabs>
                </Box>

                <Stack direction="row" spacing={2} sx={{mt: 2, alignItems: "center", justifyContent: "space-between"}}>
                    <Stack direction="row" spacing={2} alignItems="center">
                        <LocalizationProvider dateAdapter={AdapterMoment}>
                            <DatePicker
                                label="Received On"
                                value={receivedOn ? moment(receivedOn, "YYYY-MM-DD") : null} // Ensure valid moment object
                                onChange={(newValue) => {
                                    setReceivedOn(newValue ? moment(newValue).format("YYYY-MM-DD") : "");
                                }}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        size="small"
                                        InputLabelProps={{
                                            shrink: !!receivedOn
                                        }}
                                    />
                                )}
                                sx={{minWidth: 120, maxWidth: 200}}
                                disableFuture // Disable future dates
                            />
                        </LocalizationProvider>

                        <FormControl size="small" sx={{minWidth: 240}}>
                            <InputLabel id="status-select-label">Select Status</InputLabel>
                            <Select labelId="status-select-label" id="status-select" value={status} label="Select Status" onChange={(e) => setStatus(e.target.value)}>
                                <MenuItem value="">All</MenuItem>
                                <MenuItem value="To Do">To Do</MenuItem>
                                <MenuItem value="In Progress">In Progress</MenuItem>
                                <MenuItem value="Under Review">Under Review</MenuItem>
                                <MenuItem value="Completed">Completed</MenuItem>
                            </Select>
                        </FormControl>
                    </Stack>

                    <Stack direction="row" spacing={2}>
                        <TextField
                            sx={{
                                borderRadius: "40px",
                                backgroundColor: "white",
                                border: "1px solid #E0E0E0",
                                "& .MuiOutlinedInput-notchedOutline": {
                                    border: "none"
                                },
                                "&:hover .MuiOutlinedInput-notchedOutline": {
                                    border: "none"
                                }
                            }}
                            size="small"
                            placeholder="Search"
                            value={searchTerm}
                            onChange={(e) => searchHandler(e)}
                            InputProps={{
                                startAdornment: <SearchIcon sx={{mr: 1, color: "text.secondary"}}/>
                            }}
                        />
                        <Button variant="outlined" component="span" disabled={loading} startIcon={<Download/>} onClick={handleDownloadRequestsSpreadsheet}>
                            Download Requests
                        </Button>
                    </Stack>
                </Stack>

                {(tab === 0 && pendingRequests.length === 0) || (tab === 1 && completedRequests.length === 0) ? (
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                            height: "100%",
                            mt: 8
                        }}
                    >
                        <Typography
                            variant="h4"
                            color="text.secondary"
                            sx={{
                                mb: 2,
                                fontWeight: 600
                            }}
                        >
                            No requests found
                        </Typography>
                        <Typography variant="body1" color="text.secondary">
                            Please check back later or contact your administrator.
                        </Typography>
                    </Box>
                ) : (
                    <Grid container sx={{p: 2, borderRadius: "4px"}}>
                        <Grid item xs={tab === 1 ? 1.7 : 2}>
                            <Typography variant="subtitle2" color="text.secondary">
                                Tenant Name
                            </Typography>
                        </Grid>
                        <Grid item xs={tab === 1 ? 1.7 : 2}>
                            <Typography variant="subtitle2" color="text.secondary">
                                Filing Type
                            </Typography>
                        </Grid>
                        <Grid item xs={tab === 1 ? 1.7 : 2}>
                            <Typography variant="subtitle2" color="text.secondary">
                                PCT Number
                            </Typography>
                        </Grid>                        
                        <Grid item xs={tab === 1 ? 1.7 : 2}>
                            <Typography variant="subtitle2" color="text.secondary">
                                Request ID
                            </Typography>
                        </Grid>
                        <Grid item xs={tab === 1 ? 1.7 : 2}>
                            <Typography variant="subtitle2" color="text.secondary">
                                Request Time
                            </Typography>
                        </Grid>
                        {tab === 1 && (
                            <Grid item xs={1.5}>
                                <Typography variant="subtitle2" color="text.secondary">
                                    Completion Time
                                </Typography>
                            </Grid>
                        )}
                        <Grid item xs={1}>
                            <Typography variant="subtitle2" color="text.secondary">
                                Status
                            </Typography>
                        </Grid>
                        <Grid item xs={1}>
                            <Typography variant="subtitle2" color="text.secondary">
                                Action
                            </Typography>
                        </Grid>
                    </Grid>
                )}
                <Box
                    sx={{
                        maxHeight: 480,
                        overflow: "auto",
                        "&::-webkit-scrollbar": {
                            width: "0.4em"
                        },
                        "&::-webkit-scrollbar-track": {
                            "-webkit-box-shadow": "inset 0 0 6px rgba(0,0,0,0.00)"
                        },
                        "&::-webkit-scrollbar-thumb": {
                            backgroundColor: "rgba(0,0,0,0.1)",
                            borderRadius: "10px"
                        }
                    }}
                >
                    {paginatedRequests.map((request) => {
                        const disabledButton = request.ro101Status !== "Available" && request.isa210Status !== "Available" && request.isa237Status !== "Available" && request.initialPublicationStatus !== "Available" && request.iasrStatus !== "Available" && request.isa210XMLStatus !== "Available"  && request.iasrPDFStatus !== "Available" && request.tisrPDFStatus !== "Available" && request.tisrXMLStatus !== "Available" && request.ib371Status !== "Available" && request.declarationStatus !== "Available" && request.initialPubWithoutISRStatus !== "Available" && request.ib301ReceiptCopyStatus !== "Available" && request.ib306ChangeRecordStatus !== "Available" && request.ro105AppNoDateStatus !== "Available"

                        const saveButtonDisabled = request.ro101Status === "Pending" || request.isa210Status === "Pending" || request.isa237Status === "Pending" || request.initialPublication === "Pending" || request.iasrStatus === "Pending" || request.isa210XMLStatus === "Pending"  || request.iasrPDFStatus === "Pending" || request.tisrPDFStatus === "Pending" || request.tisrXMLStatus === "Pending" || request.ib371Status === "Pending" || request.declarationStatus === "Pending" || request.initialPubWithoutISRStatus === "Pending" || request.ib301ReceiptCopyStatus === "Pending" || request.ib306ChangeRecordStatus === "Pending" || request.ro105AppNoDateStatus === "Pending"
                        return (
                        <Box sx={{mb: 2}} key={request.requestId}>
                            <Paper
                                key={request.requestId}
                                sx={{
                                    width: "100%",
                                    mb: 2,
                                    overflow: "hidden",
                                    border: expandedRequest === request.requestId ? "1px solid #0066FF" : "1px solid #E0E0E0",
                                    borderRadius: "10px",
                                    boxShadow: "0 1px 3px rgba(0,0,0,0.1)"
                                }}
                            >
                                <Grid
                                    container
                                    alignItems="center"
                                    sx={{
                                        p: 2,
                                        backgroundColor: "#FFF"
                                    }}
                                >
                                    <Grid item xs={tab === 1 ? 1.7 : 2}>
                                        <Box sx={{display: "flex", alignItems: "center"}}>
                                            <IconButton size="small" sx={{mr: 1}} onClick={() => toggleExpand(request.requestId)}>
                                                {expandedRequest === request.requestId ? <KeyboardArrowUp fontSize="small"/> : <KeyboardArrowDown fontSize="small"/>}
                                            </IconButton>
                                            <Typography variant="subtitle1" sx={{fontWeight: "medium"}}>
                                                {request.tenant
                                                        .split("-")
                                                        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                                                        .join(" ")}
                                            </Typography>
                                        </Box>
                                    </Grid>
                                    <Grid item xs={tab === 1 ? 1.7 : 2}>
                                        <Typography>{FILING_TYPE_NAME_MAP[request.filingType]}</Typography>
                                    </Grid>
                                    <Grid item xs={tab === 1 ? 1.7 : 2}>
                                        <Typography>{request.metadata.pctNumber}</Typography>
                                    </Grid>
                                    <Grid item xs={tab === 1 ? 1.7 : 2}>
                                        <Typography variant="subtitle1" sx={{fontWeight: "medium"}}>
                                            {request.requestId}
                                        </Typography>
                                    </Grid>

                                    <Grid item xs={tab === 1 ? 1.7 : 2}>
                                        <Typography variant="body2">{`${formatDate(request.createdAt)} ${new Intl.DateTimeFormat("en-US", {hour: "2-digit", minute: "2-digit", hour12: true}).format(new Date(request.createdAt))}`}</Typography>
                                    </Grid>

                                    {tab === 1 && (
                                        <Grid item xs={1.5}>
                                            <Typography variant="body2">{`${formatDate(request.updatedAt)} ${new Intl.DateTimeFormat("en-US", {hour: "2-digit", minute: "2-digit", hour12: true}).format(new Date(request.updatedAt))}`}</Typography>
                                        </Grid>
                                    )}

                                    <Grid item xs={1}>
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                color: getStatusColor(request.status),
                                                fontWeight: "medium"
                                            }}
                                            grey
                                        >
                                            {request.status}
                                        </Typography>
                                    </Grid>

                                    <Grid item xs={1}>
                                        <IconButton size="small" disabled={loading || disabledButton} onClick={() => downloadFiles(request.requestId)} sx={{color: "primary.main"}}>
                                            <GetApp/>
                                        </IconButton>
                                    </Grid>
                                </Grid>

                                {expandedRequest === request.requestId && (
                                    <Box>
                                        <TableContainer
                                            sx={{
                                                bgcolor: "white",
                                                // px: 2,
                                                maxHeight: 380,
                                                overflow: "auto",
                                                "&::-webkit-scrollbar": {
                                                    width: "0.4em"
                                                },
                                                "&::-webkit-scrollbar-track": {
                                                    "-webkit-box-shadow": "inset 0 0 6px rgba(0,0,0,0.00)"
                                                },
                                                "&::-webkit-scrollbar-thumb": {
                                                    backgroundColor: "rgba(0,0,0,0.1)",
                                                    borderRadius: "10px"
                                                }
                                            }}
                                        >
                                            <Table size="small" sx={{}}>
                                                <TableHead>
                                                    <TableRow sx={{"& th": {borderBottom: "none", borderTop: "1px solid #E0E0E0", color: "text.secondary", paddingTop: 2}}}>
                                                        <TableCell sx={{width: "20%", textAlign: "left"}}>Document</TableCell>
                                                        <TableCell sx={{width: "20%", textAlign: "center"}}>Status</TableCell>
                                                        <TableCell sx={{width: "20%", textAlign: "center"}}>Action</TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {DOCUMENTS_LIST.map((document) => {
                                                        const field = documentFieldMap[document];
                                                        const isAvailable = request[field] === "Available";
                                                        const isNA = request[field] === "N/A";
                                                        const isPresent = request[field] === "Present";
                                                        return (
                                                            <TableRow key={document} sx={{"& td": {borderBottom: "none"}}}>
                                                                <TableCell sx={{py: 1.5, width: "20%", textAlign: "left"}}>{document}</TableCell>
                                                                <TableCell sx={{py: 1.5, width: "20%", textAlign: "center"}}>{isPresent ? <CheckCircleOutlineIcon color="success"/> : isAvailable ? <CheckCircleOutlineIcon sx={{color: "primary.main"}}/> : isNA ? <Chip label="N/A" sx={{border: "2px solid lightgrey", borderRadius: 2, color: "text.secondary"}}/> : <CheckCircleOutlineIcon color="disabled"/>}</TableCell>
                                                                <TableCell sx={{py: 1.5, width: "20%", textAlign: "center"}}>
                                                                    <IconButton size="small" sx={{color: "primary.main"}} disabled={loading || isPresent || request.status === "Completed"} onClick={() => handleUploadClick(request, request.requestId, document)}>
                                                                        <UploadFile/>
                                                                    </IconButton>
                                                                    <IconButton size="small" sx={{color: "primary.main"}} disabled={loading || !isAvailable} onClick={() => downloadFiles(request.requestId, document)}>
                                                                        <GetApp/>
                                                                    </IconButton>
                                                                </TableCell>
                                                            </TableRow>
                                                        );
                                                    })}
                                                </TableBody>
                                            </Table>
                                            <Box display={"flex"} justifyContent={"flex-end"} pr={4}>
                                                {request.status !== "Completed" &&
                                                    (request.status === "Under Review" ? (
                                                        <Button variant="contained" color="primary" disabled={loading} sx={{my: 1}} onClick={() => handleSubmit(request)}>
                                                            Submit
                                                        </Button>
                                                    ) : (
                                                        <Button variant="contained" color="primary" disabled={loading || saveButtonDisabled } sx={{my: 1}} onClick={() => handleUpdateStatus(request)}>
                                                            Save
                                                        </Button>
                                                    ))}
                                            </Box>
                                        </TableContainer>
                                    </Box>
                                )}
                            </Paper>
                        </Box>
                    )})}
                </Box>
            </Box>

            <Grid container alignItems="center" justifyContent="space-between" sx={{mt: 2}}>
                <Grid item xs={4}/>

                <Grid item xs={4} container justifyContent="center">
                    <Pagination count={Math.ceil(filteredRequests.length / rowsPerPage)} page={page} onChange={(e, value) => setPage(value)} color="primary" siblingCount={1} boundaryCount={1}/>
                </Grid>

                <Grid item xs={4} container justifyContent="flex-end">
                    <FormControl size="small">
                        <Select
                            value={rowsPerPage}
                            onChange={(e) => {
                                setRowsPerPage(parseInt(e.target.value, 10));
                                setPage(1);
                            }}
                        >
                            <MenuItem value={5}>5</MenuItem>
                            <MenuItem value={10}>10</MenuItem>
                            <MenuItem value={25}>25</MenuItem>
                        </Select>
                    </FormControl>
                    <Typography variant="body2" sx={{ml: 1, alignSelf: "center"}}>
                        Rows Per Page
                    </Typography>
                </Grid>
            </Grid>
            {modalOpen && <UploadDocModal open={modalOpen} onClose={handleCloseModal} requestId={selectedRequestId} loadRequests={loadRequests} document={selectedDocument} documentKey={documentFieldMap[selectedDocument]}/>}
        </>
    );
};

export default FilingPackageTable;